import { useState } from "react";
import { UserCheck2 } from "lucide-react";
import { type AssignableUserDto } from "@/api/users";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";
import { Button } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { useDialogs } from "@/hooks/useDialogs";

interface AssigneePickerProps {
  /** Current assignee user id (raw Identity id). */
  currentUserId?: string | null;
  /** Optional CSV of role codes to filter the dropdown (e.g. "C5,C0,Admin"). */
  rolesFilter?: string;
  /** Allow editing? When false, only the read-only display is rendered. */
  canEdit: boolean;
  /** Label shown above the readout, e.g. "Assigned marketer". */
  label: string;
  /** Called with the selected user id once the user confirms the change. */
  onAssign: (userId: string) => Promise<void>;
  /** When true, the assign mutation is in flight and the controls are disabled. */
  busy?: boolean;
}

/**
 * Ownership/Assignment §4 / §12 — read-only display + reassign dropdown.
 * Reused by Lead detail and Broker detail pages. Backed by the shared
 * useAssignableUsers hook (rolesFilter applied client-side).
 */
export function AssigneePicker({
  currentUserId,
  rolesFilter,
  canEdit,
  label,
  onAssign,
  busy,
}: AssigneePickerProps) {
  const dialogs = useDialogs();
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<string>("");

  const users = useAssignableUsers(rolesFilter, {
    enabled: !!currentUserId || (canEdit && editing),
  });

  const current = users.data?.find((u: AssignableUserDto) => u.id === currentUserId);
  const display = current?.displayName ?? currentUserId?.slice(0, 8) ?? "—";

  if (!editing) {
    return (
      <div className="flex items-center gap-space-2">
        <div className="flex items-center gap-space-1 text-small text-onyx">
          <UserCheck2 size={14} strokeWidth={1.5} aria-hidden />
          <span className="text-caption uppercase tracking-wide text-gray-mid">{label}:</span>
          <span>{display}</span>
        </div>
        {canEdit && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => { setPending(currentUserId ?? ""); setEditing(true); }}
            disabled={busy}
          >
            Reassign
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-space-2">
      <Select
        value={pending}
        onChange={(e) => setPending(e.target.value)}
        disabled={users.isLoading || busy}
        className="max-w-xs"
      >
        <option value="" disabled>Select a user…</option>
        {users.data?.map((u) => (
          <option key={u.id} value={u.id}>
            {u.displayName} ({u.roleCodes.join(", ") || "no role"})
          </option>
        ))}
      </Select>
      <Button
        size="sm"
        onClick={async () => {
          if (!pending || pending === currentUserId) {
            setEditing(false);
            return;
          }
          const target = users.data?.find((u) => u.id === pending);
          const ok = await dialogs.confirm({
            title: "Reassign?",
            message: `Assign ${target?.displayName ?? pending} as the new ${label.toLowerCase()}?`,
            confirmLabel: "Reassign",
          });
          if (!ok) return;
          await onAssign(pending);
          setEditing(false);
        }}
        disabled={busy || !pending || pending === currentUserId}
      >
        {busy ? "Saving…" : "Save"}
      </Button>
      <Button size="sm" variant="tertiary" onClick={() => setEditing(false)} disabled={busy}>
        Cancel
      </Button>
    </div>
  );
}
